/**
 * Provides a context for managing state and selectors related to multi-station data visualization.
 * @module MultiStationContext
 */
import React, { createContext, useContext, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addOrSubtractDaysToTimestamp } from '@awp/helpers/Utils';

import {
  selectStatus,
  selectWeatherStation,
  selectWeatherStationId,
  selectHeatMapObservation,
  selectSnowPoleObservation,
  selectPresetId,
  selectDisplayDateRange,
  setDisplayDateRange,
  derivedObservatioIds,
} from '@stores/dataVisualizationSlice';
import { getGraphPresetById } from '@stores/graphPresetSlice';
import { selectAllWeatherStations } from '@awp/stores/weatherStationSlice';
import { countGraphPresetDatasets, getMaxDataRange } from '@awp/helpers/utilsReact';
import { isDeferOnlyPreset } from '@awp/components/Graphs/MultiStation/helpers';
/**
 * Context for managing state and selectors related to multi-station data visualization.
 * @type {React.Context}
 */
const MultiStationContext = createContext();

/**
 * Provider component for MultiStationContext.
 * @param {object} props - Component props.
 * @param {boolean} props.isForGraph - Initial flag to indicate if it's for graph.
 * @param {React.ReactNode} props.children - Child components wrapped by the provider.
 * @returns {React.ReactNode} Provider component.
 */
export const MultiStationContextProvider = ({ isForGraph: initialIsForGraph = false, children }) => {
  const dispatch = useDispatch();

  // State level values used in multi-station context
  // State value with the initial prop
  const [isForGraph, setIsForGraph] = useState(initialIsForGraph);
  const [showInvalid, setShowInvalid] = useState(false);
  const [showLegend, setShowLegend] = useState(true);
  const [syncGraphs, setSyncGraphs] = useState(true);
  const [showReticle, setShowReticle] = useState(false);
  const [lastClickedXValue, setLastClickedXValue] = useState(sessionStorage.getItem('lastClickedXValue'));
  const [lastHoveredXValue, setLastHoveredXValue] = useState(sessionStorage.getItem('lastHoveredXValue'));

  const dataRange = useSelector(selectDisplayDateRange);
  const [timelineValues, setTimelineValues] = useState({
    min: addOrSubtractDaysToTimestamp(dataRange.endDate, -dataRange.displayRange),
    max: dataRange.endDate,
    left: 0,
    right: 0,
  });
  const [forecastWeatherStationId, setForecastWeatherStationId] = useState(null);
  const [maxDays, setMaxDays] = useState(null);
  const [isSelectedDeferOnlyPreset, setIsSelectedDeferOnlyPreset] = useState(false);
  // const [showHeatMap, setShowHeatMap] = useState(false);

  // Data visualization selector variables used in multi-station context
  const status = useSelector(selectStatus);
  const presetId = useSelector(selectPresetId);
  const weatherStationId = useSelector(selectWeatherStationId);
  const weatherStation = useSelector(selectWeatherStation);
  const allWeatherStations = useSelector(selectAllWeatherStations);
  const selectedDerivedObservatioIds = useSelector(derivedObservatioIds);
  const currentPreset = useSelector((state) => getGraphPresetById(state, presetId)); //preset object not the dropdown preset
  const heatMapObservation = useSelector(selectHeatMapObservation);
  const snowPoleObservation = useSelector(selectSnowPoleObservation);
  const setEndDate = (dateValue) => {
    dispatch(setDisplayDateRange({ endDate: Math.floor(dateValue), displayRange: dataRange.displayRange }));
  };

  const updateDataRange = (dateValue, displayRange) => {
    dispatch(setDisplayDateRange({ endDate: Math.floor(dateValue), displayRange: displayRange }));
  };

  const currentPresetFiltered = useMemo(() => {
    if (!currentPreset?.DataGraphs) return currentPreset;

    return {
      ...currentPreset,
      DataGraphs: currentPreset.DataGraphs.map((dg) => ({
        ...dg,
        PersistentGraphFilters:
          dg.PersistentGraphFilters?.filter((pgf) => !selectedDerivedObservatioIds.includes(pgf.ObservationTypeId)) ??
          [],
      })),
    };
  }, [currentPreset, selectedDerivedObservatioIds]);

  const hasDeferWeatherStation = useMemo(() => {
    console.log(currentPreset);
    if (currentPreset) {
      setIsSelectedDeferOnlyPreset(isDeferOnlyPreset(currentPreset));
    }
    setMaxDays(getMaxDataRange(countGraphPresetDatasets(currentPreset)));
    return (
      currentPreset?.DataGraphs?.some((dg) =>
        dg.PersistentGraphFilters?.some((pgf) => pgf.WeatherStationId === null),
      ) ?? false
    );
  }, [currentPreset]);

  // Memoized values
  const weatherStations = useMemo(() => {
    let wxs = [];
    // Determine which weather stations are in the preset
    wxs = Array.from(
      new Set(
        currentPreset?.DataGraphs?.flatMap((dg) => dg.PersistentGraphFilters)
          .map((pgf) => {
            return allWeatherStations.find((wx) => wx.Id === pgf.WeatherStationId);
          })
          .filter((p) => p != undefined),
      ),
    );

    if (
      (weatherStationId ?? -1) !== -1 && // no longer add deferred station to list
      !wxs.some((e) => e.Id === weatherStationId) // Make sure weather station isn't already in the list
    ) {
      wxs.push(allWeatherStations.find((wx) => wx.Id === weatherStationId));
    }

    return wxs;
  }, [weatherStationId, presetId]);

  // Structure the context values for the provider
  const stateValues = {
    presetId,
    showInvalid,
    showLegend,
    syncGraphs,
    showReticle,
    timelineValues,
    dataRange,
    //showHeatMap,
    forecastWeatherStationId,
    currentPreset,
    maxDays,
    isForGraph,
    isSelectedDeferOnlyPreset,
    lastClickedXValue,
    lastHoveredXValue,
  };

  const stateSetters = {
    //setPresetId,
    setShowInvalid,
    setShowLegend,
    setSyncGraphs,
    setShowReticle,
    setTimelineValues,
    setEndDate,
    updateDataRange,
    //setShowHeatMap,
    setForecastWeatherStationId,
    setMaxDays,
    setIsForGraph,
    setLastClickedXValue,
    setLastHoveredXValue,
  };

  const selectorValues = {
    status,
    weatherStationId,
    weatherStation,
    preset: currentPresetFiltered,
    heatMapObservation,
    snowPoleObservation,
    presetId,
    currentPreset,
  };

  const memoizedValues = {
    weatherStations,
    hasDeferWeatherStation,
  };

  // Combine values into a single context value
  const contextValue = {
    ...stateValues,
    ...stateSetters,
    ...selectorValues,
    ...memoizedValues,
  };

  return <MultiStationContext.Provider value={contextValue}>{children}</MultiStationContext.Provider>;
};

/**
 * Hook for consuming MultiStationContext.
 * @returns {object} Context value.
 */
export const useMultiStationContext = () => useContext(MultiStationContext);
